import type {
    ICovidCertificateEncryptionCmd,
    ICovidCertificateVerifyCommand,
} from "@/data/commands/profile/CovidEncryptionCmd";
import type { IRemoveCreditCardCmd } from "@/data/commands/profile/RemoveCreditCardCmd";
import type { IUpdateCovidInfoCmd } from "@/data/commands/profile/UpdateCovidInfoCmd";
import type { IUpdateProfileCmd } from "@/data/commands/profile/UpdateProfileCmd";
import type { ISetOperatorSettingsCmd } from "@/data/commands/user/SetOperatorSettingsCmd";
import type { ICreditCard } from "@/data/model/CreditCard";
import type { ILocation } from "@/data/model/Location";
import type { ICovidCertificateEncryptionResult, ICovidCertificateVerifyResult, IProfile } from "@/data/model/Profile";
import type { IOperatorSettings } from "@/data/model/User";
import { DateTime } from "@/data/util/DateTime";

import { BaseServiceDao, parseCollection } from "./BaseServiceDao";
import { UserDao } from "./UserDao";

export class ProfileDao extends BaseServiceDao {
    public static className = "ProfileDao";

    constructor() {
        super("profile");
    }

    public async getProfile(): Promise<IProfile> {
        return this.get("", undefined, ProfileDao.parseProfile);
    }

    public async getLocationGrants(): Promise<Array<ILocation>> {
        return this.get("grant/", undefined);
    }

    public async updateProfile(cmd: IUpdateProfileCmd): Promise<IProfile> {
        return this.post("", cmd, ProfileDao.parseProfile, true);
    }

    public async updateCovidInfo(cmd: IUpdateCovidInfoCmd): Promise<IProfile> {
        return this.put("covid", cmd, ProfileDao.parseProfile);
    }

    public async sendEmailConfirmation(): Promise<void> {
        return this.post("sendemailconfirm");
    }

    public async getCreditCards(): Promise<Array<ICreditCard>> {
        return this.get("creditcard/", undefined, (s) => parseCollection(s, ProfileDao.parseCreditCard));
    }

    public async removeCreditCard(id: string, cmd: IRemoveCreditCardCmd): Promise<void> {
        return this.delete("creditcard/" + id, cmd);
    }

    public async encryptCovidCert(cmd: ICovidCertificateEncryptionCmd): Promise<ICovidCertificateEncryptionResult> {
        return this.post("encryptcovidcert", cmd, ProfileDao.parseCovidCertificateEncryptionResult, true);
    }

    public async verifyCovidCert(cmd: ICovidCertificateVerifyCommand): Promise<ICovidCertificateVerifyResult> {
        return this.post("verifycovidcert", cmd, ProfileDao.parseCovidCertificateVerifyResult);
    }

    public async getOperatorSettings(): Promise<IOperatorSettings | undefined> {
        return this.get("operatorsettings/", undefined, UserDao.parseOperatorSettings);
    }

    public async setOperatorSettings(data: ISetOperatorSettingsCmd): Promise<IOperatorSettings | undefined> {
        return this.put("operatorsettings/", data, UserDao.parseOperatorSettings);
    }

    public static parseProfile(literal: any): IProfile {
        return {
            identityId: literal.identityId,
            authenticatedAs: literal.authenticatedAs != null ? literal.authenticatedAs : undefined,
            avatar: literal.avatar != null ? literal.avatar : undefined,
            identityCard: literal.identityCard != null ? literal.identityCard : undefined,
            firstName: literal.firstName != null ? literal.firstName : undefined,
            lastName: literal.lastName != null ? literal.lastName : undefined,
            email: literal.email != null ? literal.email : undefined,
            phoneNumber: literal.phoneNumber != null ? literal.phoneNumber : undefined,
            zip: literal.zip != null ? literal.zip : undefined,
            birthday: literal.birthday != null ? DateTime.parseIsoDate(literal.birthday) : undefined,
            hasConfirmedMail: literal.hasConfirmedMail,
            creditCards: parseCollection(literal.creditCards, ProfileDao.parseCreditCard),
            lastCovidCertValidation:
                literal.lastCovidCertValidation == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.lastCovidCertValidation),
            roles: literal.roles,
            operatorLocations: literal.operatorLocations,
        };
    }

    public static parseCreditCard(literal: any): ICreditCard {
        return {
            id: literal.id,
            provider: literal.provider == null ? undefined : literal.provider,
            brand: literal.brand == null ? undefined : literal.brand,
            holder: literal.holder == null ? undefined : literal.holder,
            info: literal.info == null ? undefined : literal.info,
            expiryDate: literal.expiryDate == null ? undefined : DateTime.parseIsoDate(literal.expiryDate),
            created: DateTime.parseIsoDate(literal.created),
        };
    }

    public static parseCovidCertificateVerifyResult(literal: any): ICovidCertificateVerifyResult {
        return {
            firstName: literal.firstName == null ? undefined : literal.firstName,
            lastName: literal.lastName == null ? undefined : literal.lastName,
            birthday: literal.birthday == null ? undefined : DateTime.parseIsoDate(literal.birthday),
            passedModes: parseCollection(literal.passedModes, (c) => parseInt(c)),
            isValid: literal.isValid,
        };
    }

    public static parseCovidCertificateEncryptionResult(literal: any): ICovidCertificateEncryptionResult {
        return {
            ...ProfileDao.parseCovidCertificateVerifyResult(literal),
            identityCard: literal.identityCard,
            hash: literal.hash,
            secondaryHash: literal.secondaryHash == null ? undefined : literal.secondaryHash,
        };
    }
}
